import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useRef } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getCategoriesHub, type DBCategory, type DBDiscovery } from "@/lib/content.functions";
import { resolveImage } from "@/lib/asset-map";

export const Route = createFileRoute("/categories")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["categories-hub"],
      queryFn: () => getCategoriesHub(),
    }),
  head: () => {
    const url = "https://smartasaurus.com/categories";
    const title = "Browse Categories · Smartasaurus";
    const desc =
      "Every Smartasaurus rabbit hole - weird animals, oceans, dangerous nature, science facts, space, the human body, and unexplained mysteries.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CategoriesHub,
  errorComponent: ({ reset }) => {
    const router = useRouter();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div>
          <p className="text-muted-foreground">Couldn't load categories.</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-bold uppercase text-primary-foreground">Retry</button>
        </div>
      </div>
    );
  },
});

function CategoryRail({ category, items }: { category: DBCategory; items: DBDiscovery[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollBy = (dx: number) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  };
  return (
    <section className="group/rail relative">
      <div className="mb-3 flex items-end justify-between gap-4 px-4 md:px-8">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· {category.title}</div>
          <Link
            to="/category/$slug"
            params={{ slug: category.slug }}
            className="mt-1 block truncate font-display text-2xl uppercase leading-none transition hover:text-primary md:text-3xl"
          >
            {category.label}
          </Link>
        </div>
        <Link
          to="/category/$slug"
          params={{ slug: category.slug }}
          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground transition hover:text-primary"
        >
          See all <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="relative">
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => scrollBy(-600)}
          className="absolute left-1 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-black/60 p-2 text-foreground opacity-0 backdrop-blur transition group-hover/rail:opacity-100 md:block"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => scrollBy(600)}
          className="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-black/60 p-2 text-foreground opacity-0 backdrop-blur transition group-hover/rail:opacity-100 md:block"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        <div
          ref={scrollerRef}
          className="no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 pb-2 md:gap-4 md:px-8"
        >
          {items.map((d) => {
            const img = resolveImage(d.image_url);
            return (
              <Link
                key={d.id}
                to="/discovery/$slug"
                params={{ slug: d.slug }}
                className="group/card relative w-[70vw] max-w-[280px] shrink-0 snap-start overflow-hidden rounded-xl border border-border bg-card transition hover:border-primary/50 sm:w-[300px] md:w-[320px]"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  {img ? (
                    <img
                      src={img}
                      alt={d.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition duration-700 group-hover/card:scale-110"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-curiosity opacity-40" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <h3 className="line-clamp-2 font-display text-sm uppercase leading-tight text-foreground group-hover/card:text-primary md:text-base">
                      {d.title}
                    </h3>
                  </div>
                </div>
              </Link>
            );
          })}
          <Link
            to="/category/$slug"
            params={{ slug: category.slug }}
            className="flex w-[40vw] max-w-[180px] shrink-0 snap-start items-center justify-center rounded-xl border border-dashed border-border bg-card/50 text-center text-[11px] font-bold uppercase tracking-widest text-muted-foreground transition hover:border-primary hover:text-primary sm:w-[180px]"
          >
            See all<br />in {category.label}
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategoriesHub() {
  const { categories: allCategories, discoveries } = Route.useLoaderData() as { categories: DBCategory[]; discoveries: DBDiscovery[] };
  const byCat = new Map<string, DBDiscovery[]>();
  for (const d of discoveries) {
    if (!d.category_id) continue;
    const arr = byCat.get(d.category_id) ?? [];
    arr.push(d);
    byCat.set(d.category_id, arr);
  }
  const categories = allCategories.filter((c) => (byCat.get(c.id)?.length ?? 0) > 0);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-4 pb-10 md:px-8 md:pb-14">
          <div className="border-b border-border pb-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Categories</div>
            <h1 className="mt-2 font-display text-4xl uppercase leading-none md:text-6xl">
              Pick a rabbit hole
            </h1>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              Every category is its own hub. Scroll through discoveries, or dive into any category for the full collection.
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] space-y-14 md:space-y-16">
          {categories.map((c) => (
            <CategoryRail key={c.id} category={c} items={(byCat.get(c.id) ?? []).slice(0, 14)} />
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
